/**
 * @prettier
 **/
// @ts-check

import {
  publisherService,
  columnsFetchService,
  accountSettingsService 
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import {
  cloneDeep,
  assign,
  isEmpty,
  forOwn,
  forEach,
  map,
  filter
} from 'lodash';
import {
  SET_PUBLISHERS,
  SET_PUBLISHER,
  SET_PUBLISHER_REFERENCES,
  SET_CHECK_REFERENCES_TEXT,
  SET_WIDGET_TITLE as SET_WIDGET_TITLE_MUTATION,
  SET_USER_VISIBLE_COLUMNS,
  SET_ACCOUNT_SETTINGS
} from './mutationTypes';
import {
  GET_PUBLISHERS,
  GET_PUBLISHER,
  CREATE_PUBLISHER,
  UPDATE_PUBLISHER,
  GET_PUBLISHER_REFERENCES,
  DELETE_PUBLISHER,
  SET_INVITES_FOR_PUBLISHER,
  SET_SHOW_NEW_PUBLISHER_INVITES,
  SAVE_PUBLISHER,
  SET_WIDGET_TITLE,
  GET_USER_VISIBLE_COLUMNS,
  GET_ACCOUNT_SETTINGS
} from './actionTypes';
import {
  SET_ACTION_STATUS_PENDING,
  SET_ACTION_STATUS_SUCCESS,
  SET_ACTION_STATUS_ERROR
} from '@/store/helpers';

const emptyStats = {
  total: 0,
  currentMonth: 0,
  currentDay: 0,
  currentHour: 0,
  currentLive: 0
};

const fillStatsData = (affiliates, stats) => {
  const {
    affiliates: updatedAffiliates,
    updatedAffiliatesStats
  } = normalizeStats(affiliates, stats);
  updatedAffiliates.forEach((affiliate) => {
    if (updatedAffiliatesStats[affiliate.id]) {
      assign(affiliate, updatedAffiliatesStats[affiliate.id]);
    } else {
      assign(affiliate, emptyStats);
    }
  });
  return updatedAffiliates;
};

const normalizeStats = (affiliates, stats) => {
  const updatedAffiliatesStats = {};
  if (!isEmpty(stats)) {
    forOwn(stats, (value, key) => {
      const upperFirstLetterkey = key.charAt(0).toUpperCase() + key.slice(1);
      updatedAffiliatesStats[upperFirstLetterkey] = value;
    });
  } else {
    forEach(affiliates, (affiliate) => {
      updatedAffiliatesStats[affiliate.id] = { ...emptyStats };
    });
  }
  return { affiliates, updatedAffiliatesStats };
};

const getStats = async (affiliates, accountId) => {
  const response = await publisherService.getAffiliatesStats(accountId);
  return fillStatsData(affiliates, response.data.stats);
};

/** @type {import('vuex').ActionTree} */
const actions = {
  async [SET_WIDGET_TITLE]({ commit }, { title }) {
    commit(SET_WIDGET_TITLE_MUTATION, title);
  },

  /**
   * Get Account Settings
   */
  async [GET_ACCOUNT_SETTINGS]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_ACCOUNT_SETTINGS });
    try {
      const response = await accountSettingsService.loadSettings(rootState.currentAccountId);
      const accountSettings = response.data.accountSettings;
      commit(SET_ACCOUNT_SETTINGS, accountSettings);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_ACCOUNT_SETTINGS });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_ACCOUNT_SETTINGS, error: e });
    }
  },


  /**
   * Get all Publishers
   */
  async [GET_PUBLISHERS]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_PUBLISHERS });
    try {
      const response = await publisherService.getAffiliates(
        rootState.currentAccountId,
        true
      );
      const publishers = fillStatsData(
        response.data.affiliates,
        response.data.stats
      );
      commit(SET_PUBLISHERS, publishers);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_PUBLISHERS });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_PUBLISHERS, error: e });
    }
  },

  /**
   * Get a publisher
   */
  async [GET_PUBLISHER]({ rootState, commit, state }, { pubId }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_PUBLISHER });
    commit(SET_PUBLISHERS, []);
    try {
      const response = await publisherService.getAffiliate(
        rootState.currentAccountId,
        pubId
      );
      const publishers = await getStats(
        [response.data.affiliate],
        rootState.currentAccountId
      );
      const columns = columnsFetchService.getColumnList(
        {},
        publisherService.defaultColumns,
        'publishers',
        state.columnSettings
      );
      commit(SET_PUBLISHERS, publishers);
      commit(SET_USER_VISIBLE_COLUMNS, columns);
      commit(SET_WIDGET_TITLE_MUTATION, `Publishers: ${publishers[0].name}`);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_PUBLISHER });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_PUBLISHER, error });
    }
  },

  /**
   * Get publisher references
   */
  async [GET_PUBLISHER_REFERENCES]({ rootState, commit }, { pubId }) {
    commit(
      SET_CHECK_REFERENCES_TEXT,
      'DELETE_AFFILIATE_MODAL_CHECK_ASSOCIATION_TEXT'
    );
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_PUBLISHER_REFERENCES });
    commit(SET_PUBLISHER_REFERENCES, null);
    try {
      const response = await publisherService.getAffiliateReferences(
        rootState.currentAccountId,
        pubId
      );

      commit(SET_PUBLISHER_REFERENCES, response.data);
      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: GET_PUBLISHER_REFERENCES
      });
    } catch (error) {
      commit(
        SET_CHECK_REFERENCES_TEXT,
        'DELETE_AFFILIATE_MODAL_CHECK_ASSOCIATION_ERROR_TEXT'
      );
      commit(SET_ACTION_STATUS_ERROR, {
        actionName: GET_PUBLISHER_REFERENCES,
        error
      });
    }
  },

  /**
   * Delete a publisher
   */
  async [DELETE_PUBLISHER](
    { rootState, commit, state },
    { pubId, keepNumbers }
  ) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: DELETE_PUBLISHER });
    try {
      await publisherService.deleteAffiliate(
        rootState.currentAccountId,
        pubId,
        true,
        keepNumbers
      );
      const arrayToModify = cloneDeep(state.publishers);
      arrayToModify.splice(
        state.publishers.findIndex((b) => b.id === pubId),
        1
      );
      commit(SET_PUBLISHERS, arrayToModify);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: DELETE_PUBLISHER });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: DELETE_PUBLISHER, error });
    }
  },

  /**
   * Update a publisher
   */
  async [UPDATE_PUBLISHER]({ rootState, commit }, { publisher }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: UPDATE_PUBLISHER });
    try {
      const response = await publisherService.updateAffiliate(
        rootState.currentAccountId,
        publisher
      );
      commit(SET_PUBLISHER, { publisher: response.data.affiliate });
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: UPDATE_PUBLISHER });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: UPDATE_PUBLISHER, error });
    }
  },

  /**
   * Create a publisher
   */
  async [CREATE_PUBLISHER]({ rootState, commit, dispatch }, { publisher }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CREATE_PUBLISHER });
    try {
      const response = await publisherService.createAffiliate(
        rootState.currentAccountId,
        publisher
      );
      await dispatch(SAVE_PUBLISHER, { publisher: response.data.affiliates });
      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: CREATE_PUBLISHER,
        data: response.data.affiliates
      });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CREATE_PUBLISHER, error });
    }
  },

  async [SAVE_PUBLISHER]({ commit }, { publisher }) {
    commit(SET_INVITES_FOR_PUBLISHER, { publisher });
    commit(SET_SHOW_NEW_PUBLISHER_INVITES, { showNewPublisherInvites: true });
  },

  [SET_INVITES_FOR_PUBLISHER]({ commit }, { publisher }) {
    commit(SET_INVITES_FOR_PUBLISHER, { publisher });
  },

  [SET_SHOW_NEW_PUBLISHER_INVITES]({ commit }, { showNewPublisherInvites }) {
    commit(SET_SHOW_NEW_PUBLISHER_INVITES, { showNewPublisherInvites });
  },

  /*
   * Get user visible columns
   */
  async [GET_USER_VISIBLE_COLUMNS]({ commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_USER_VISIBLE_COLUMNS });
    commit(SET_USER_VISIBLE_COLUMNS, []);
    try {
      const userVisibleColumns = map(
        filter(publisherService.columnSettings, (col) => !col.hide),
        (col) => {
          return col.name;
        }
      );
      commit(SET_USER_VISIBLE_COLUMNS, userVisibleColumns);
      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: GET_USER_VISIBLE_COLUMNS
      });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, {
        actionName: GET_USER_VISIBLE_COLUMNS,
        error: e
      });
    }
  }
};

export default actions;
